import {
  Box,
  Heading,
  Text,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react';

type CookieRow = {
  name: string;
  provider: string;
  purpose: string;
  duration: string;
  type: string;
};

type CookieCategory = {
  title: string;
  rows?: CookieRow[];
  note?: string;
};

const cookieCategories: CookieCategory[] = [
  {
    title: 'Necessary Cookies',
    rows: [
      {
        name: 'CookieConsent',
        provider: 'Cookiebot by Usercentrics',
        purpose:
          "Stores the user's cookie consent state for the current domain.",
        duration: '365 days',
        type: 'HTTP Cookie',
      },
    ],
  },
  {
    title: 'Preference',
    rows: [
      {
        name: 'chakra-ui-color-mode',
        provider: 'Merkatus',
        purpose:
          'Necessary for the functionality of the website chatbox function.',
        duration: 'Persistent',
        type: 'HTML Local Storage',
      },
    ],
  },
  {
    title: 'Statistics',
    rows: [
      {
        name: 'Amplitude',
        provider: 'Amplitude',
        purpose:
          "Registers statistical data on users' behaviour on the website. Used for internal analytics by the website operator.",
        duration: '365 days',
        type: 'HTTP Cookie',
      },
      {
        name: 'Sentry',
        provider: 'Sentry',
        purpose:
          "Registers data on visitors' website behaviour. This is used for internal analysis and website optimization.",
        duration: 'Persistent',
        type: 'HTML Local Storage',
      },
    ],
  },
  {
    title: 'Marketing',
    rows: [
      {
        name: '',
        provider: '',
        purpose: '',
        duration: '',
        type: '',
      },
    ],
  },
];

const CookiePolicyPage = () => {
  return (
    <Box
      bg="white"
      color="gray.800"
      maxW="756px"
      mx="auto"
      px={6}
      pt={16}
      pb={24}
      fontFamily="marketingPage"
      fontSize="15px"
      lineHeight="24px">
      <VStack align="start" spacing={16}>
        {/* Header */}
        <Box>
          <Heading as="h1" fontSize="40px" mb={4} fontFamily="marketingPage">
            Cookie Policy
          </Heading>
          <Text fontSize="sm" color="gray.600">
            Last updated: November 13, 2025
          </Text>
        </Box>
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            Annex 1 - Cookies
          </Heading>
          <VStack align="start" spacing={6}>
            <Box>
              <Heading
                as="h3"
                fontSize="24px"
                mb={3}
                fontFamily="marketingPage">
                1. What Are Cookies?
              </Heading>
              <VStack align="start" spacing={3} lineHeight="24px">
                <Text>
                  Cookies are small files that can be used to improve your user
                  experience on our website. The cookies are stored on your
                  computer or mobile device when you visit our site.
                </Text>
                <Text>
                  There are two main types of cookies, session cookies and
                  permanent cookies. Session cookies are temporary cookies and
                  are deleted after your visit to our website when you close the
                  browser. Permanent cookies are stored for a longer time period
                  depending on their purpose.
                </Text>
                <Text>
                  Depending on your preferences, we use cookies and similar
                  technologies on our website to improve user experience by
                  personalizing the content you see, to improve our service by
                  analysing traffic on our website and the functioning of our
                  features, and for marketing purposes.
                </Text>
                <Text>
                  Some cookies are so-called third-party cookies placed on our
                  site by third parties.
                </Text>
              </VStack>
            </Box>

            <Box>
              <Heading
                as="h3"
                fontSize="24px"
                mb={3}
                fontFamily="marketingPage">
                2. What Kind of Cookies Do We Use?
              </Heading>
              <VStack align="start" spacing={3} lineHeight="24px">
                <Text>
                  <strong>Necessary cookies:</strong> Necessary cookies
                  implement functions that are essential for the functioning and
                  proper use of the website. For example, these cookies enable
                  proper navigation on the website by saving your login
                  information while you browse from one page to another on our
                  website.
                </Text>
                <Text>
                  <strong>Preference cookies:</strong> Preference cookies enable
                  us to personalise the website according to your preferences.
                  For example, they are used to save information about past
                  choices you made on the website, such as your language
                  settings.
                </Text>
                <Text>
                  <strong>Statistics cookies:</strong> Statistics cookies help
                  us to understand how our visitors use the website. These
                  cookies help us compile statistics on how visitors move around
                  the website and which pages on our website are popular. We use
                  this information to improve our website.
                </Text>
                <Text>
                  <strong>Marketing cookies:</strong> Marketing cookies make it
                  possible for us to display ads on our website that are
                  relevant to you. These cookies track your behaviour across
                  different websites.
                </Text>
              </VStack>
            </Box>

            <Box>
              <Heading
                as="h3"
                fontSize="24px"
                mb={3}
                fontFamily="marketingPage">
                3. When Do We Use Cookies?
              </Heading>
              <Text lineHeight="24px">
                Necessary cookies are required for the proper functioning of the
                website and cannot be turned off. We use all other cookies only
                based on your consent. You can accept the use of all cookies or
                only specific categories of cookies or withdraw your consent in
                the cookie settings in the Cookie Declaration on our website.
                You may also control or delete cookies through your browser
                settings at any time, but this may affect the functioning of the
                website.
              </Text>
            </Box>

            <Box w="full">
              <Heading
                as="h3"
                fontSize="24px"
                mb={3}
                fontFamily="marketingPage">
                4. Overview of Cookies on Our Website
              </Heading>
              <VStack align="start" spacing={6} w="full">
                {cookieCategories.map((category) => (
                  <Box key={category.title} w="full">
                    <Heading
                      as="h4"
                      fontSize="20px"
                      mt={4}
                      mb={2}
                      fontFamily="marketingPage">
                      {category.title}
                    </Heading>
                    {category.rows && category.rows.length > 0 ? (
                      <TableContainer overflowX="auto">
                        <Table
                          variant="simple"
                          size="sm"
                          sx={{
                            tableLayout: 'fixed',
                            'th, td': { fontSize: '13px', lineHeight: '20px' },
                          }}>
                          <Thead>
                            <Tr>
                              <Th whiteSpace="normal" wordBreak="break-word">
                                Name
                              </Th>
                              <Th whiteSpace="normal" wordBreak="break-word">
                                Provider
                              </Th>
                              <Th whiteSpace="normal" wordBreak="break-word">
                                Purpose
                              </Th>
                              <Th whiteSpace="normal" wordBreak="break-word">
                                Storage Duration
                              </Th>
                              <Th whiteSpace="normal" wordBreak="break-word">
                                Type
                              </Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {category.rows.map((row) => (
                              <Tr key={`${category.title}-${row.name}`}>
                                <Td whiteSpace="normal" wordBreak="break-word">
                                  {row.name}
                                </Td>
                                <Td whiteSpace="normal" wordBreak="break-word">
                                  {row.provider}
                                </Td>
                                <Td whiteSpace="normal" wordBreak="break-word">
                                  {row.purpose}
                                </Td>
                                <Td whiteSpace="normal" wordBreak="break-word">
                                  {row.duration}
                                </Td>
                                <Td whiteSpace="normal" wordBreak="break-word">
                                  {row.type}
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </TableContainer>
                    ) : null}
                  </Box>
                ))}
              </VStack>
            </Box>
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
};

export default CookiePolicyPage;
